import { ListRequest } from "@/common/common_utils";
import { X4bActivityStatus } from "@/services/signalr.service";
import { XpressVehicleType } from "./VehicleType";
import { Flag } from "@/modules/authentication/sign-up/components/CountryFlags";
import { X4bLocation } from "@/modules/concierge/views/PlacesRepository";

export interface X4bActivityPointDto extends X4bLocation {
	Id: number;
	ActivityId: number;

	SortOrder?: number | null;

}
// .
export interface X4bProgramDto {
	Id: number;
	Name?: string;
}
export interface GetActivityListRequest extends ListRequest {
	Date?: Date;
	Status?: string;
	ProgramIds?: number[];
	Amount?: number;
}

/** Xpress `GetServicesAndPrices` / concierge flows: ride vs delivery product (matches `XpressServiceType` on the API). */
export enum XpressServiceType {
	Ride = 1,
	Delivery = 2,
}

/** When `GetServicesAndPrices` returns no `VehicleTypes`, the route is not serviceable. */
export const X4B_GET_SERVICES_NO_VEHICLES_MESSAGE =
	'This route is not in a serviceable area. Please choose different pickup or drop-off locations.';

export interface GetServicesAndPricesRequest {
	Points: X4bActivityPointDto[];
	ServiceTypeId: XpressServiceType;
}


export interface GetServicesAndPricesResponse {
	VehicleTypes: VehicleTypeDto[];
	GoogleMapsRouting: string | null;
}

export interface VehicleTypeDto {
	FareId: string;
	VehicleTypeId: XpressVehicleType;
	Amount: number;
	IsActive: boolean;
}
export enum BookingType {
	Scheduled = 1,
	Asap = 2,
	Flexible = 3
}

/** Mirrors `Xpress.DB.Enums.X4b.DeliveryItemType` / `DeliveryOptions.DeliveryItemTypeId`. */
export enum DeliveryItemType {
	None = 0,
	Document = 1,
	Food = 2,
	Clothing = 3,
	Electronic = 4,
	Fragile = 5,
	Medical = 6,
	Other = 7,
}

/** Mirrors `Xpress.DB.Enums.X4b.DeliverySize` / `DeliveryOptions.DeliverySize`. */
export enum DeliverySize {
	None = 0,
	Small = 1,
	Medium = 2,
}

export interface PassengerDto {
	FirstName?: string;
	LastName?: string;
	Phone?: string;
	Country?: Flag;
}

/** Send-booking parcel / contact payload — matches `DeliveryOptions` on X4B `PlaceOrderRequest`. */
export interface DeliveryOptions {
	DeliveryRecipientName?: string;
	DeliveryRecipientPhone?: string;
	DeliverySenderName?: string;
	DeliverySenderPhone?: string;
	DeliveryWeight?: number | null;
	DeliveryItemTypeId?: DeliveryItemType | null;
	DeliverySize?: DeliverySize;
	PackagePhotoBase64?: string;
}

export interface PlaceOrderRequest {
	XpressId: string;
	FareId: string;
	Amount: number;
	VehicleType: XpressVehicleType;
	BookingType: BookingType;
	ScheduledAt?: string | null;
	TimeZoneOffset: number;
	NoteToDriver?: string;
	Points: X4bActivityPointDto[];
	HeadPassenger: PassengerDto;
	AdditionalPassengers: PassengerDto[];
	Memo?: string;
	Note?: string;
	GoogleMapsRouting?: string | null;
	/** Send booking only; omit for ride-only `PlaceOrder`. */
	DeliveryOptions?: DeliveryOptions;
	/** Persisted server-side for rebook pricing. Defaults to ride when omitted. */
	ServiceTypeId?: XpressServiceType;
}

export interface GetGoogleTokenResponse {
	Token: string;
	ExpiresInSeconds: number;
	GoogleTripId: string;
}

export interface GetGoogleMapRoutingResponse {
	GoogleMapRouting?: string;
}

export enum X4bVehicleColor {
	Unknown = 0,
	DarkBlue = 1,
	Red = 2,
	Yellow = 3,
	Green = 4,
	Silver = 5,
	Brown = 6,
	White = 7,
	Black = 8,
	Orange = 9,
	Purple = 10,
	Blue = 11,
	Maroon = 12,
	DarkGreen = 13,
	Gray = 14,
	Bronze = 15,
	Magenta = 16,
	LightBlue = 17
}
export function getVehicleColorStyle(color: X4bVehicleColor | undefined): string {
	switch (color) {
		case X4bVehicleColor.DarkBlue:
			return 'darkblue';
		case X4bVehicleColor.Red:
			return 'red';
		case X4bVehicleColor.Yellow:
			return 'yellow';
		case X4bVehicleColor.Green:
			return 'green';
		case X4bVehicleColor.Silver:
			return 'silver';
		case X4bVehicleColor.Brown:
			return 'saddlebrown';
		case X4bVehicleColor.White:
			return 'white';
		case X4bVehicleColor.Black:
			return 'black';
		case X4bVehicleColor.Orange:
			return 'orange';
		case X4bVehicleColor.Purple:
			return 'purple';
		case X4bVehicleColor.Blue:
			return 'blue';
		case X4bVehicleColor.Maroon:
			return 'maroon';
		case X4bVehicleColor.DarkGreen:
			return 'darkgreen';
		case X4bVehicleColor.Gray:
			return 'gray';
		case X4bVehicleColor.Bronze:
			return '#CD7F32';
		case X4bVehicleColor.Magenta:
			return 'magenta';
		case X4bVehicleColor.LightBlue:
			return 'lightblue';
		case X4bVehicleColor.Unknown:
		default:
			return 'lightgray';
	}
}
export interface DriverDto {
	Id: number;
	Name: string;
	Plate: string;
	Vehicle: string;
	Phone: string;
	Rating: number;
	VehicleColor?: X4bVehicleColor;
}

export interface GetOrderDetailsRequest {
	BusinessOrderId: string;
}

export interface OrderPriceBreakdownDto {
	BaseFare: number;
	DistanceInM: number;
	DistanceFare: number;
	TimeFare: number;
	SurgeCharges: number;
	PromoAmount: number | null;
	TotalAmount: number;
}

export interface OrderAttemptDto {
	DateTime: string;
	ActionText: string;
}

export interface GetOrderDetailsResponse {
	PriceBreakdown: OrderPriceBreakdownDto | null;
	Attempts: OrderAttemptDto[] | null;
	AttemptsCount: number;
}
